import React, { useState, useEffect } from "react";
import { auth } from "./firebase.js";
import { toast } from "react-toastify";

const InvoicesPage = ({ isMobile }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoices = async () => {
      const user = auth.currentUser;
      if (!user) {
        toast.error("Please log in to view invoices");
        setLoading(false);
        return;
      }

      try {
        const idToken = await user.getIdToken();
        console.log("Calling getInvoices with fetch");
        const response = await fetch(
          "https://us-central1-nantahala-mountain-resor-596d9.cloudfunctions.net/api/getInvoices",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${idToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ ownerId: user.uid }),
          }
        );

        console.log("getInvoices response status:", response.status);
        console.log("getInvoices response headers:", [...response.headers]);

        if (!response.ok) {
          const errorData = await response.json();
          console.error("getInvoices response error:", errorData);
          throw new Error(errorData.error || "Failed to fetch invoices");
        }

        const data = await response.json();
        console.log("getInvoices response:", data);

        // Filter out invoices with status "draft"
        const filteredInvoices = (data.invoices || []).filter(
          (invoice) => invoice.status !== "draft"
        );
        console.log("Filtered invoices (excluding drafts):", filteredInvoices);

        setInvoices(filteredInvoices);
      } catch (error) {
        console.error("Error fetching invoices:", error.message);
        toast.error(`Error fetching invoices: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  if (loading) {
    return <div className="loading-message">Loading invoices...</div>;
  }

  return (
    <div className="page-container">
      <style>
        {`
          .page-container {
            padding: ${isMobile ? "1rem" : "2rem"};
          }
          .page-title {
            font-size: ${isMobile ? "1.25rem" : "1.5rem"};
            margin-bottom: 1rem;
          }
          .table-container {
            overflow-x: auto;
            position: relative;
            z-index: 1;
          }
          .contractor-table {
            min-width: 100%;
            border-collapse: collapse;
          }
          .table-header {
            background: #1a3c34;
            color: white;
          }
          .table-header-cell {
            border: 1px solid #d1d5db;
            padding: ${isMobile ? "0.4rem" : "0.5rem"};
            text-align: left;
            white-space: nowrap;
            font-size: ${isMobile ? "0.875rem" : "1rem"};
          }
          .table-row:nth-child(even) {
            background: #f3f4f6;
          }
          .table-row:nth-child(odd) {
            background: #e5e7eb;
          }
          .table-cell {
            border: 1px solid #d1d5db;
            padding: ${isMobile ? "0.4rem" : "0.5rem"};
            white-space: pre-line;
            font-size: ${isMobile ? "0.875rem" : "1rem"};
          }
          .no-invoices {
            border: 1px solid #d1d5db;
            padding: ${isMobile ? "0.4rem" : "0.5rem"};
            text-align: center;
            font-size: ${isMobile ? "0.875rem" : "1rem"};
          }
          .loading-message {
            border: 1px solid #d1d5db;
            padding: ${isMobile ? "0.4rem" : "0.5rem"};
            text-align: center;
            font-size: ${isMobile ? "0.875rem" : "1rem"};
          }
          .pay-now-link {
            color: #3b82f6;
            text-decoration: underline;
          }
          .pay-now-link:hover {
            color: #2563eb;
          }
        `}
      </style>
      <h1 className="page-title">Your Invoices</h1>
      <div className="table-container">
        <table className="contractor-table">
          <thead className="table-header">
            <tr>
              <th className="table-header-cell">Invoice Number</th>
              <th className="table-header-cell">Owner Name</th>
              <th className="table-header-cell">Email</th>
              <th className="table-header-cell">Address</th>
              <th className="table-header-cell">Amount Due</th>
              <th className="table-header-cell">Created</th>
              <th className="table-header-cell">Due Date</th>
              <th className="table-header-cell">Status</th>
              <th className="table-header-cell">Description</th>
              <th className="table-header-cell">Action</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan="10" className="no-invoices">
                  No finalized invoices found.
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr key={invoice.id} className="table-row">
                  <td className="table-cell">{invoice.number || "N/A"}</td>
                  <td className="table-cell">{invoice.name1 || "N/A"}</td>
                  <td className="table-cell">{invoice.customer_email || "N/A"}</td>
                  <td className="table-cell">{invoice.customer_address || "N/A"}</td>
                  <td className="table-cell">
                    {(invoice.amount_due / 100).toLocaleString("en-US", {
                      style: "currency",
                      currency: invoice.currency || "USD",
                    })}
                  </td>
                  <td className="table-cell">
                    {invoice.created ? new Date(invoice.created * 1000).toLocaleDateString() : "N/A"}
                  </td>
                  <td className="table-cell">
                    {invoice.due_date ? new Date(invoice.due_date * 1000).toLocaleDateString() : "N/A"}
                  </td>
                  <td className="table-cell">{invoice.status}</td>
                  <td className="table-cell">{invoice.description || "N/A"}</td>
                  <td className="table-cell">
                    {invoice.hosted_invoice_url && (
                      <a
                        href={invoice.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pay-now-link"
                      >
                        Pay Now
                      </a>
                    )}
                    {invoice.invoice_pdf && (
                      <a
                        href={invoice.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pay-now-link"
                        style={{ marginLeft: "10px" }}
                      >
                        Download PDF
                      </a>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoicesPage;